import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { paymentUploadAPI, reportAPI, aiAPI } from '../services/api';
import Icon from '../components/Icon';
import { 
  downloadCSVTemplate, 
  downloadRecruiterPaymentsCSV, 
  downloadRecruiterPaymentHistory,
  downloadUploadDetails,
  downloadFinancialReport
} from '../utils/csvUtils';
import FinancialAnalyticsDashboard from '../components/FinancialAnalyticsDashboard';

function FinancialDashboard() {
  return (
    <Routes>
      <Route path="/" element={<FinancialAnalyticsDashboard />} />
      <Route path="/uploads" element={<PaymentUploads />} />
      <Route path="/uploads/:id" element={<UploadDetails />} />
      <Route path="/recruiters" element={<RecruiterPayments />} />
      <Route path="/recruiters/:id" element={<RecruiterPaymentDetails />} />
      <Route path="/reports" element={<PaymentReports />} />
    </Routes>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentUploads, setRecentUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsResponse, uploadsResponse] = await Promise.all([
        paymentUploadAPI.getStats(),
        paymentUploadAPI.getAll()
      ]);
      setStats(statsResponse.data);
      setRecentUploads(uploadsResponse.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            💰
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Financial Dashboard...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching payment statistics and recent uploads</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Financial Dashboard</h1>
          <p className="page-subtitle">Manage commission payments, upload payment records, and track financial metrics</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Link to="/financial/uploads" className="btn btn-secondary btn-sm">View All Uploads</Link>
          <Link to="/financial/uploads" className="btn btn-primary btn-sm">Upload Payments</Link>
        </div>
      </div>

      {/* Financial Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Payments Processed</div>
            <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>💰</div>
          </div>
          <div className="stat-card-value">KES {stats?.totalPaymentsProcessed?.toLocaleString() || 0}</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Recruiters Paid</div>
            <div className="stat-card-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>👥</div>
          </div>
          <div className="stat-card-value">{stats?.totalRecruiterspaid || 0}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Active recipients
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Pending Payments</div>
            <div className="stat-card-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>⏳</div>
          </div>
          <div className="stat-card-value">KES {stats?.pendingPayments?.toLocaleString() || 0}</div>
          <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginTop: '8px' }}>
            Awaiting processing
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Average Payment</div>
            <div className="stat-card-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>📊</div>
          </div>
          <div className="stat-card-value">KES {stats?.averagePaymentAmount?.toLocaleString() || 0}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Per transaction
          </div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="dashboard-grid">
        <div className="chart-container">
          <div className="card-header">
            <div>
              <h2 className="card-title">Monthly Payment Trends</h2>
              <p className="card-subtitle">Payment amounts and transaction counts over time</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.monthlyPayments || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" fontSize={12} fontWeight={600} />
              <YAxis stroke="#64748b" fontSize={12} fontWeight={600} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  fontWeight: '600'
                }} 
              />
              <Line 
                type="monotone" 
                dataKey="amount" 
                stroke="#10b981" 
                strokeWidth={3}
                name="Amount (KES)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Payment Methods</h2>
                <p className="card-subtitle">Distribution of payment channels</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={stats?.paymentMethods || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="count"
                >
                  {(stats?.paymentMethods || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'][index % 4]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
              {(stats?.paymentMethods || []).map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'][index % 4] }}></div>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>{item.method}</span>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'][index % 4] }}>{item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Quick Actions</h2>
                <p className="card-subtitle">Common financial operations</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link to="/financial/uploads" className="btn btn-primary" style={{ justifyContent: 'flex-start' }}>
                📤 Upload Payment CSV
              </Link>
              <Link to="/financial/recruiters" className="btn btn-secondary" style={{ justifyContent: 'flex-start' }}>
                👥 View Recruiter Payments
              </Link>
              <Link to="/financial/reports" className="btn btn-outline" style={{ justifyContent: 'flex-start' }}>
                📊 Generate Report
              </Link>
              <button 
                className="btn btn-outline" 
                style={{ justifyContent: 'flex-start' }}
                onClick={() => {
                  // This would export general financial records
                  toast.info('General export feature - would export comprehensive financial data');
                }}
              >
                📋 Export Records
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Uploads */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Recent Payment Uploads</h2>
            <p className="card-subtitle">Latest CSV uploads and processing status</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              className="btn btn-outline btn-sm"
              onClick={() => {
                downloadCSVTemplate();
                toast.success('CSV template downloaded!');
              }}
            >
              📥 Download Template
            </button>
            <Link to="/financial/uploads" className="btn btn-outline btn-sm">View All</Link>
          </div>
        </div>
        
        {recentUploads.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Upload Date</th>
                  <th>Records</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentUploads.map((upload) => (
                  <tr key={upload.id}>
                    <td>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{upload.filename}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{upload.notes}</div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>
                          {new Date(upload.upload_date).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {formatTimeAgo(upload.upload_date)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '16px', fontWeight: '700' }}>{upload.total_records}</span>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          <div>✅ {upload.successful_records}</div>
                          <div>❌ {upload.failed_records}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: '700', color: '#10b981', fontSize: '16px' }}>
                        KES {upload.total_amount.toLocaleString()}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${
                        upload.upload_status === 'completed' ? 'badge-success' : 
                        upload.upload_status === 'processing' ? 'badge-warning' : 'badge-danger'
                      }`}>
                        {upload.upload_status === 'completed' ? 'Completed' : 
                         upload.upload_status === 'processing' ? 'Processing' : 'Failed'}
                      </span>
                    </td>
                    <td>
                      <Link 
                        to={`/financial/uploads/${upload.id}`}
                        className="btn btn-outline btn-sm"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.5 }}>📤</div>
            <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Payment Uploads Yet</h3>
            <p style={{ fontSize: '14px', marginBottom: '24px' }}>
              Upload your first CSV file with payment records to get started
            </p>
            <Link to="/financial/uploads" className="btn btn-primary">Upload Payment CSV</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function PaymentUploads() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadUploads();
  }, []);

  const loadUploads = async () => {
    try {
      const response = await paymentUploadAPI.getAll();
      setUploads(response.data);
    } catch (error) {
      toast.error('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await paymentUploadAPI.upload(formData);
      toast.success('Payment file uploaded successfully! Processing...');
      loadUploads();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            📤
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Payment Uploads...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Payment Uploads</h1>
          <p className="page-subtitle">Upload and manage CSV files containing recruiter payment records</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <button 
            className="btn btn-outline btn-sm"
            onClick={() => {
              downloadCSVTemplate();
              toast.success('CSV template downloaded successfully!');
            }}
          >
            Download Template
          </button>
          <label className={`btn btn-primary btn-sm ${uploading ? 'loading' : ''}`}>
            {uploading ? 'Uploading...' : '📤 Upload CSV'}
            <input 
              type="file" 
              accept=".csv" 
              onChange={handleFileUpload} 
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {/* Upload Instructions */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">CSV Upload Instructions</h2>
            <p className="card-subtitle">Required format for payment upload files</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '12px' }}>
              Required Columns
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>recruiter_email:</strong> Recruiter's email address
              </li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>referral_code:</strong> Recruiter's referral code
              </li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>amount_paid:</strong> Payment amount (positive number)
              </li>
              <li style={{ padding: '8px 0', fontSize: '14px' }}>
                <strong>payment_method:</strong> M-Pesa, Bank Transfer, etc.
              </li>
            </ul>
          </div>
          
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '12px' }}>
              Optional Columns
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>payment_reference:</strong> Transaction reference
              </li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>payment_date:</strong> Date of payment (YYYY-MM-DD)
              </li>
              <li style={{ padding: '8px 0', fontSize: '14px' }}>
                <strong>notes:</strong> Additional notes
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Uploads Table */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Upload History</h2>
            <p className="card-subtitle">All payment file uploads and their processing status</p>
          </div>
        </div>
        
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>File Details</th>
                <th>Upload Info</th>
                <th>Processing Results</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((upload) => (
                <tr key={upload.id}>
                  <td>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                        {upload.filename}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {upload.notes}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>
                        {new Date(upload.upload_date).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {new Date(upload.upload_date).toLocaleTimeString()}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#64748b' }}>{upload.total_records}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Total</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>{upload.successful_records}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Success</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#ef4444' }}>{upload.failed_records}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Failed</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                      KES {upload.total_amount.toLocaleString()}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${
                      upload.upload_status === 'completed' ? 'badge-success' : 
                      upload.upload_status === 'processing' ? 'badge-warning' : 'badge-danger'
                    }`}>
                      {upload.upload_status === 'completed' ? 'Completed' : 
                       upload.upload_status === 'processing' ? 'Processing' : 'Failed'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Link 
                        to={`/financial/uploads/${upload.id}`}
                        className="btn btn-outline btn-sm"
                      >
                        View Details
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UploadDetails() {
  // This would be implemented to show detailed view of a specific upload
  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Upload Details</h1>
          <p className="page-subtitle">Detailed view of payment upload processing</p>
        </div>
      </div>
      <div className="card">
        <p>Upload details implementation would go here...</p>
      </div>
    </div>
  );
}

function PaymentReports() {
  const [reportType, setReportType] = useState('summary');
  const [dateRange, setDateRange] = useState('last_30_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [narrative, setNarrative] = useState('');
  const [narrating, setNarrating] = useState(false);
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);

  const reportTypes = [
    { value: 'summary', label: 'Payment Summary Report', description: 'Overview of all payments and commissions' },
    { value: 'detailed', label: 'Detailed Payment Report', description: 'Line-by-line payment transactions' },
    { value: 'recruiter_performance', label: 'Recruiter Performance Report', description: 'Individual recruiter earnings and metrics' },
    { value: 'monthly_trends', label: 'Monthly Trends Report', description: 'Payment trends and patterns over time' },
    { value: 'payment_methods', label: 'Payment Methods Report', description: 'Breakdown by payment channels' },
    { value: 'pending_payments', label: 'Pending Payments Report', description: 'Outstanding commission payments' },
    { value: 'audit_trail', label: 'Payment Audit Trail', description: 'Complete payment processing history' }
  ];

  const dateRanges = [
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_90_days', label: 'Last 90 Days' },
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'current_quarter', label: 'Current Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'current_year', label: 'Current Year' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = { dateRange };
      if (dateRange === 'custom') {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      const res = await reportAPI.getFinancialReport(params);
      setReportData({ ...res.data, reportType });

      toast.success(`${reportTypes.find(r => r.value === reportType)?.label} generated successfully!`);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const generateMockReportData = (type, range) => {
    console.warn('generateMockReportData is deprecated; real data is fetched from /api/reports/financial');
    return { reportType: type };
  };

  const generateNarrative = async () => {
    if (!reportData) {
      toast.error('Please generate a report first');
      return;
    }
    setNarrating(true);
    try {
      const title = reportTypes.find((r) => r.value === reportType)?.label || 'Payment Report';
      const res = await aiAPI.reportNarrative({ title, dateRange, data: reportData });
      setNarrative(res.data?.narrative || 'No narrative generated.');
      toast.success('AI narrative generated');
    } catch (e) {
      toast.error(e.message || 'Failed to generate narrative');
    } finally {
      setNarrating(false);
    }
  };

  const clearNarrative = () => setNarrative('');

  const exportReport = (format) => {
    if (!reportData) {
      toast.error('Please generate a report first');
      return;
    }

    const reportName = reportTypes.find(r => r.value === reportType)?.label || 'Payment Report';
    const timestamp = new Date().toISOString().split('T')[0];

    if (format === 'csv') {
      downloadFinancialReport(reportData, reportName, reportType);
      toast.success(`${reportName} exported as CSV successfully!`);
    } else if (format === 'pdf') {
      // Simulate PDF generation
      toast.info('PDF export feature - would generate comprehensive PDF report with charts and graphs');
    } else if (format === 'excel') {
      // Simulate Excel generation
      toast.info('Excel export feature - would generate detailed Excel workbook with multiple sheets and pivot tables');
    }
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Payment Reports</h1>
          <p className="page-subtitle">Generate comprehensive financial reports and analytics</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Link to="/financial" className="btn btn-outline btn-sm">← Back to Dashboard</Link>
          {reportData && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => exportReport('csv')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                📊 Export CSV
              </button>
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => exportReport('excel')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                📈 Export Excel
              </button>
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => exportReport('pdf')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                📄 Export PDF
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={generateNarrative}
                disabled={narrating}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Icon name="help" /> {narrating ? 'Generating…' : '✨ AI Narrative'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Configuration */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Report Configuration</h2>
            <p className="card-subtitle">Select report type, date range, and filters</p>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          {/* Report Type Selection */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              Report Type
            </label>
            <select 
              className="form-select"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
            >
              {reportTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
              {reportTypes.find(r => r.value === reportType)?.description}
            </p>
          </div>

          {/* Date Range Selection */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              Date Range
            </label>
            <select 
              className="form-select"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              {dateRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>

          {/* Custom Date Range */}
          {dateRange === 'custom' && (
            <>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  End Date
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
          <button 
            className={`btn btn-primary ${loading ? 'loading' : ''}`}
            onClick={generateReport}
            disabled={loading}
          >
            {loading ? 'Generating Report...' : '📊 Generate Report'}
          </button>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">
                {reportTypes.find(r => r.value === reportType)?.label}
              </h2>
              <p className="card-subtitle">
                Generated on {new Date(reportData.generatedAt).toLocaleString()} • 
                {dateRanges.find(d => d.value === dateRange)?.label}
              </p>
            </div>
          </div>

          {narrative && (
            <div style={{ marginBottom: '24px', padding: '16px', background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(59,130,246,0.06))', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="help" /> AI Narrative
                </h3>
                <button className="btn btn-outline btn-sm" onClick={clearNarrative}>Dismiss</button>
              </div>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'var(--text-primary)' }}>{narrative}</p>
            </div>
          )}

          {/* Summary Report */}
          {reportData.reportType === 'summary' && (
            <div>
              <div className="stats-grid" style={{ marginBottom: '32px' }}>
                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-card-title">Total Earned</div>
                    <div className="stat-card-icon" style={{ background: 'rgba(3, 123, 144, 0.1)', color: '#037b90' }}>💰</div>
                  </div>
                  <div className="stat-card-value">KES {reportData.summary.totalCommissionsEarned.toLocaleString()}</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                    Commission earnings
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-card-title">Total Paid</div>
                    <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>✅</div>
                  </div>
                  <div className="stat-card-value">KES {reportData.summary.totalPaymentsMade.toLocaleString()}</div>
                  <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '600', marginTop: '8px' }}>
                    {reportData.summary.paymentSuccessRate}% success rate
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-card-title">Pending</div>
                    <div className="stat-card-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>⏳</div>
                  </div>
                  <div className="stat-card-value">KES {reportData.summary.pendingPayments.toLocaleString()}</div>
                  <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginTop: '8px' }}>
                    Awaiting payment
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-card-title">Top Method</div>
                    <div className="stat-card-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>📱</div>
                  </div>
                  <div className="stat-card-value" style={{ fontSize: '18px' }}>{reportData.summary.topPaymentMethod}</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                    Most used method
                  </div>
                </div>
              </div>

              <div className="table-container">
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Monthly Breakdown</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Total Payments</th>
                      <th>Transactions</th>
                      <th>Active Recruiters</th>
                      <th>Average per Recruiter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.monthlyBreakdown.map((month, index) => (
                      <tr key={index}>
                        <td style={{ fontWeight: '600' }}>{month.month}</td>
                        <td style={{ fontWeight: '700', color: '#037b90' }}>
                          KES {month.payments.toLocaleString()}
                        </td>
                        <td>{month.transactions}</td>
                        <td>{month.recruiters}</td>
                        <td style={{ fontWeight: '600' }}>
                          KES {Math.round(month.payments / month.recruiters).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Detailed Report */}
          {reportData.reportType === 'detailed' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Recruiter</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.transactions.map((txn) => (
                    <tr key={txn.id}>
                      <td>
                        <div style={{ fontWeight: '500' }}>
                          {new Date(txn.date).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {new Date(txn.date).toLocaleTimeString()}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600' }}>{txn.recruiterName}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{txn.referralCode}</div>
                      </td>
                      <td style={{ fontWeight: '700', color: '#037b90' }}>
                        KES {txn.amount.toLocaleString()}
                      </td>
                      <td>{txn.method}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{txn.reference}</td>
                      <td>
                        <span className={`badge ${txn.status === 'Completed' ? 'badge-success' : 'badge-danger'}`}>
                          {txn.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recruiter Performance Report */}
          {reportData.reportType === 'recruiter_performance' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Recruiter</th>
                    <th>Total Earned</th>
                    <th>Total Paid</th>
                    <th>Pending</th>
                    <th>Payment Efficiency</th>
                    <th>Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.recruiters.map((recruiter, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ fontWeight: '600' }}>{recruiter.name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{recruiter.code}</div>
                      </td>
                      <td style={{ fontWeight: '700', color: '#037b90' }}>
                        KES {recruiter.earned.toLocaleString()}
                      </td>
                      <td style={{ fontWeight: '700', color: '#10b981' }}>
                        KES {recruiter.paid.toLocaleString()}
                      </td>
                      <td style={{ fontWeight: '700', color: '#f59e0b' }}>
                        KES {recruiter.pending.toLocaleString()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            width: '60px', 
                            height: '6px', 
                            backgroundColor: '#e2e8f0', 
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{ 
                              width: `${recruiter.efficiency}%`, 
                              height: '100%', 
                              backgroundColor: recruiter.efficiency > 90 ? '#10b981' : recruiter.efficiency > 80 ? '#f59e0b' : '#ef4444',
                              borderRadius: '3px'
                            }}></div>
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: '600' }}>{recruiter.efficiency}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          recruiter.efficiency > 90 ? 'badge-success' : 
                          recruiter.efficiency > 80 ? 'badge-warning' : 'badge-danger'
                        }`}>
                          {recruiter.efficiency > 90 ? 'Excellent' : 
                           recruiter.efficiency > 80 ? 'Good' : 'Needs Attention'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Monthly Trends Report */}
          {reportData.reportType === 'monthly_trends' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Payment Trends Analysis</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Total Payments</th>
                        <th>Transactions</th>
                        <th>Growth Rate</th>
                        <th>Active Recruiters</th>
                        <th>Avg per Transaction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trends?.map((trend, index) => (
                        <tr key={index}>
                          <td style={{ fontWeight: '600' }}>{trend.month}</td>
                          <td style={{ fontWeight: '700', color: '#037b90' }}>
                            KES {Math.round(trend.payments).toLocaleString()}
                          </td>
                          <td>{trend.transactions}</td>
                          <td>
                            <span style={{ 
                              color: trend.growthRate > 0 ? '#10b981' : '#ef4444',
                              fontWeight: '600'
                            }}>
                              {trend.growthRate > 0 ? '+' : ''}{trend.growthRate.toFixed(1)}%
                            </span>
                          </td>
                          <td>{trend.recruiters}</td>
                          <td style={{ fontWeight: '600' }}>
                            KES {Math.round(trend.payments / trend.transactions).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods Report */}
          {reportData.reportType === 'payment_methods' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Payment Method</th>
                    <th>Transaction Count</th>
                    <th>Total Amount</th>
                    <th>Market Share</th>
                    <th>Success Rate</th>
                    <th>Avg Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.paymentMethods?.map((method, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: '600' }}>{method.method}</td>
                      <td>{method.count}</td>
                      <td style={{ fontWeight: '700', color: '#037b90' }}>
                        KES {method.amount.toLocaleString()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            width: '60px', 
                            height: '6px', 
                            backgroundColor: '#e2e8f0', 
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{ 
                              width: `${(method.amount / reportData.paymentMethods.reduce((sum, m) => sum + m.amount, 0)) * 100}%`, 
                              height: '100%', 
                              backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'][index % 4],
                              borderRadius: '3px'
                            }}></div>
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: '600' }}>
                            {((method.amount / reportData.paymentMethods.reduce((sum, m) => sum + m.amount, 0)) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td>
                        <span style={{ color: method.successRate > 95 ? '#10b981' : '#f59e0b', fontWeight: '600' }}>
                          {method.successRate.toFixed(1)}%
                        </span>
                      </td>
                      <td style={{ fontWeight: '600' }}>
                        KES {Math.round(method.amount / method.count).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending Payments Report */}
          {reportData.reportType === 'pending_payments' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Recruiter</th>
                    <th>Pending Amount</th>
                    <th>Days Outstanding</th>
                    <th>Action Required</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.pendingPayments?.map((payment, index) => (
                    <tr key={index}>
                      <td>
                        <span className={`badge ${
                          payment.priority === 'High' ? 'badge-danger' : 
                          payment.priority === 'Medium' ? 'badge-warning' : 'badge-info'
                        }`}>
                          {payment.priority}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600' }}>{payment.recruiter}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{payment.code}</div>
                      </td>
                      <td style={{ fontWeight: '700', color: '#f59e0b' }}>
                        KES {payment.amount.toLocaleString()}
                      </td>
                      <td>
                        <span style={{ 
                          color: payment.days > 20 ? '#ef4444' : payment.days > 10 ? '#f59e0b' : '#64748b',
                          fontWeight: '600'
                        }}>
                          {payment.days} days
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-outline btn-sm">
                          {payment.days > 15 ? 'Follow Up' : 'Schedule Payment'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Audit Trail Report */}
          {reportData.reportType === 'audit_trail' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Details</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.auditTrail?.slice(0, 15).map((audit, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ fontWeight: '500' }}>
                          {new Date(audit.timestamp).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {new Date(audit.timestamp).toLocaleTimeString()}
                        </div>
                      </td>
                      <td style={{ fontWeight: '600' }}>{audit.action}</td>
                      <td style={{ fontSize: '13px', color: '#64748b' }}>{audit.user}</td>
                      <td>{audit.details}</td>
                      <td>
                        <span className={`badge ${audit.status === 'Success' ? 'badge-success' : 'badge-danger'}`}>
                          {audit.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Quick Report Templates */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Quick Report Templates</h2>
            <p className="card-subtitle">Pre-configured reports for common use cases</p>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          <button 
            className="btn btn-outline"
            style={{ 
              padding: '16px', 
              textAlign: 'left', 
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
            onClick={() => {
              setReportType('summary');
              setDateRange('current_month');
              generateReport();
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: '600' }}>📊 Monthly Summary</div>
            {/* <div style={{ fontSize: '13px', color: '#64748b' }}>Current month payment overview</div> */}
          </button>

          <button 
            className="btn btn-outline"
            style={{ 
              padding: '16px', 
              textAlign: 'left', 
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
            onClick={() => {
              setReportType('pending_payments');
              setDateRange('last_30_days');
              generateReport();
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: '600' }}>⏳ Outstanding Payments</div>
            {/* <div style={{ fontSize: '13px', color: '#64748b' }}>All pending commission payments</div> */}
          </button>

          <button 
            className="btn btn-outline"
            style={{ 
              padding: '16px', 
              textAlign: 'left', 
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
            onClick={() => {
              setReportType('recruiter_performance');
              setDateRange('current_quarter');
              generateReport();
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: '600' }}>🏆 Top Performers</div>
            {/* <div style={{ fontSize: '13px', color: '#64748b' }}>Quarterly recruiter rankings</div> */}
          </button>

          <button 
            className="btn btn-outline"
            style={{ 
              padding: '16px', 
              textAlign: 'left', 
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
            onClick={() => {
              setReportType('audit_trail');
              setDateRange('last_7_days');
              generateReport();
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: '600' }}>🔍 Recent Activity</div>
            {/* <div style={{ fontSize: '13px', color: '#64748b' }}>Last 7 days payment audit</div> */}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecruiterPayments() {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    loadRecruiterPayments();
  }, [searchTerm, sortBy]);

  const loadRecruiterPayments = async () => {
    try {
      const response = await paymentUploadAPI.getRecruiterPayments(searchTerm, sortBy);
      setRecruiters(response.data);
    } catch (error) {
      toast.error('Failed to load recruiter payments');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            👥
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Recruiter Payments...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Recruiter Payments</h1>
          <p className="page-subtitle">View payment history and status for all recruiters</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <button 
            className="btn btn-outline btn-sm"
            onClick={() => {
              downloadRecruiterPaymentsCSV(recruiters);
              toast.success('Recruiter payments exported successfully!');
            }}
          >
            Export All
          </button>
          <Link to="/financial/uploads" className="btn btn-primary btn-sm">Upload Payments</Link>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '32px' }}>
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Recruiters</div>
            <div className="stat-card-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>👥</div>
          </div>
          <div className="stat-card-value">{recruiters.length}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Active recruiters
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Earned</div>
            <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>💰</div>
          </div>
          <div className="stat-card-value">
            KES {recruiters.reduce((sum, r) => sum + r.total_commissions_earned, 0).toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Total commissions
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Paid</div>
            <div className="stat-card-icon" style={{ background: 'rgba(3, 123, 144, 0.1)', color: '#037b90' }}>✅</div>
          </div>
          <div className="stat-card-value">
            KES {recruiters.reduce((sum, r) => sum + r.total_paid, 0).toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: '#037b90', fontWeight: '600', marginTop: '8px' }}>
            Payments processed
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Pending Payments</div>
            <div className="stat-card-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>⏳</div>
          </div>
          <div className="stat-card-value">
            KES {recruiters.reduce((sum, r) => sum + r.pending_amount, 0).toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginTop: '8px' }}>
            Awaiting payment
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="🔍 Search by name, email, or referral code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <select 
              className="form-select" 
              style={{ minWidth: '150px' }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort by Name</option>
              <option value="total_earned">Total Earned</option>
              <option value="total_paid">Total Paid</option>
              <option value="pending">Pending Amount</option>
              <option value="last_payment">Last Payment</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recruiters Table */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Recruiter Payment Overview</h2>
            <p className="card-subtitle">Commission earnings and payment status for all recruiters</p>
          </div>
        </div>
        
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Recruiter</th>
                <th>Performance</th>
                <th>Total Earned</th>
                <th>Total Paid</th>
                <th>Pending</th>
                <th>Last Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recruiters.map((recruiter) => (
                <tr key={recruiter.recruiter_id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '50%', 
                        background: `linear-gradient(135deg, #3b82f6, #10b981)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '14px'
                      }}>
                        {recruiter.recruiter_name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{recruiter.recruiter_name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{recruiter.recruiter_email}</div>
                        <span className="badge badge-info" style={{ fontSize: '10px', marginTop: '2px' }}>
                          {recruiter.referral_code}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>{recruiter.admissions_count}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Admissions</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>{recruiter.enrollments_count}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Enrollments</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                        KES {recruiter.total_commissions_earned.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        Total commissions
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: '700', color: '#10b981', fontSize: '16px' }}>
                        KES {recruiter.total_paid.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {recruiter.payment_count} payments
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ 
                        fontWeight: '700', 
                        color: recruiter.pending_amount > 0 ? '#f59e0b' : '#64748b', 
                        fontSize: '16px' 
                      }}>
                        KES {recruiter.pending_amount.toLocaleString()}
                      </div>
                      {recruiter.pending_amount > 0 && (
                        <div style={{ fontSize: '12px', color: '#f59e0b' }}>
                          Needs payment
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      {recruiter.last_payment_date ? (
                        <>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>
                            {new Date(recruiter.last_payment_date).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {Math.floor((new Date() - new Date(recruiter.last_payment_date)) / (1000 * 60 * 60 * 24))} days ago
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: '14px', color: '#94a3b8' }}>No payments yet</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Link 
                        to={`/financial/recruiters/${recruiter.recruiter_id}`}
                        className="btn btn-outline btn-sm"
                      >
                        View Details
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RecruiterPaymentDetails() {
  const { id } = useParams();
  const [recruiter, setRecruiter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecruiterDetails();
  }, [id]);

  const loadRecruiterDetails = async () => {
    try {
      const response = await paymentUploadAPI.getRecruiterPaymentDetails(id);
      setRecruiter(response.data);
    } catch (error) {
      toast.error('Failed to load recruiter details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            👤
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Recruiter Details...</h3>
        </div>
      </div>
    );
  }

  if (!recruiter) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Recruiter not found</h3>
          <Link to="/financial/recruiters" className="btn btn-primary" style={{ marginTop: '16px' }}>
            Back to Recruiters
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">{recruiter.recruiter_name}</h1>
          <p className="page-subtitle">Detailed payment history and commission breakdown</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Link to="/financial/recruiters" className="btn btn-outline btn-sm">← Back to List</Link>
          <button 
            className="btn btn-primary btn-sm"
            onClick={() => {
              downloadRecruiterPaymentHistory(recruiter);
              toast.success(`Payment history for ${recruiter.recruiter_name} exported successfully!`);
            }}
          >
            Export History
          </button>
        </div>
      </div>

      {/* Recruiter Summary */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '24px' }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #3b82f6, #10b981)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '32px',
            fontWeight: '700'
          }}>
            {recruiter.recruiter_name.split(' ').map(n => n[0]).join('')}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px' }}>
              {recruiter.recruiter_name}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>{recruiter.recruiter_email}</span>
              <span className="badge badge-info">{recruiter.referral_code}</span>
            </div>
            <div style={{ display: 'flex', gap: '32px' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#3b82f6' }}>
                  {recruiter.admissions_count}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Admissions</div>
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>
                  {recruiter.enrollments_count}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Enrollments</div>
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#037b90' }}>
                  KES {recruiter.total_commissions_earned.toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Total Earned</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Summary Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '32px' }}>
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Earned</div>
            <div className="stat-card-icon" style={{ background: 'rgba(3, 123, 144, 0.1)', color: '#037b90' }}>💰</div>
          </div>
          <div className="stat-card-value">KES {recruiter.total_commissions_earned.toLocaleString()}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Commission earnings
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Paid</div>
            <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>✅</div>
          </div>
          <div className="stat-card-value">KES {recruiter.total_paid.toLocaleString()}</div>
          <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '600', marginTop: '8px' }}>
            {Math.round((recruiter.total_paid / recruiter.total_commissions_earned) * 100)}% of earned
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Pending Amount</div>
            <div className="stat-card-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>⏳</div>
          </div>
          <div className="stat-card-value">KES {recruiter.pending_amount.toLocaleString()}</div>
          <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginTop: '8px' }}>
            Awaiting payment
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Payment Count</div>
            <div className="stat-card-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>📊</div>
          </div>
          <div className="stat-card-value">{recruiter.payment_count}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Total payments made
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Payment History</h2>
            <p className="card-subtitle">Complete record of all payments made to this recruiter</p>
          </div>
        </div>
        
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Payment Date</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Status</th>
                <th>Upload Batch</th>
              </tr>
            </thead>
            <tbody>
              {recruiter.payment_history.map((payment) => (
                <tr key={payment.id}>
                  <td>
                    <div>
                      {payment.payment_date ? (
                        <>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {Math.floor((new Date() - new Date(payment.payment_date)) / (1000 * 60 * 60 * 24))} days ago
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: '14px', color: '#94a3b8' }}>Pending</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                      KES {payment.amount.toLocaleString()}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      {payment.payment_method || 'Not specified'}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#64748b' }}>
                      {payment.reference || '-'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${
                      payment.status === 'completed' ? 'badge-success' : 
                      payment.status === 'pending' ? 'badge-warning' : 'badge-danger'
                    }`}>
                      {payment.status === 'completed' ? 'Completed' : 
                       payment.status === 'pending' ? 'Pending' : 'Failed'}
                    </span>
                  </td>
                  <td>
                    {payment.upload_id ? (
                      <Link 
                        to={`/financial/uploads/${payment.upload_id}`}
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Upload #{payment.upload_id}
                      </Link>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default FinancialDashboard;